import { useEffect, useState } from 'react';
import { apiFetch } from '../../pages/records/api';
import { reportCaughtInBrowser } from '../../shell/systemErrors';

/**
 * The one fetch-and-blob path every file the application draws comes through
 * (decisions 0549, 0563).
 *
 * 🔨 The bytes arrive through `apiFetch` and are drawn from a blob the browser
 * made, never by pointing a tag at the route: the deployed proxy sends
 * `object-src 'none'` and `frame-src 'self' blob:`, so a plug-in element never
 * issues a request and a frame pointed at the route is refused. A tag that
 * never loads reports no failure a page can read, which is how a shared file
 * came back as the wrong error message for every reader (decision 0549).
 *
 * 🔨 The request is the page's own, so it carries the session, the View-as
 * headers and the development bearer the way every other read does.
 *
 * 🔨 The blob is revoked when the reader leaves or the address changes. A blob
 * URL nobody revokes holds the whole file in memory for as long as the tab
 * lives.
 *
 * 💰 The whole file is in memory before any of it is drawn, because a fetch
 * does not stream where a plug-in element did: a long sheet shows nothing until
 * its last byte lands (decision 0549).
 */
export type FetchedBlob =
  /** The address has not answered yet, or there is nothing to ask for. */
  | { kind: 'fetching' }
  /**
   * The bytes are here: a blob URL, the bytes themselves, the served type and
   * the name the answer gave. 🔨 The `Blob` is handed back beside the URL so a
   * save can make a URL of its own with its own lifetime: the one drawn here is
   * revoked the moment the viewer leaves, and a transfer still running against
   * it is aborted by that (found in review, 2026-09-19).
   */
  | { kind: 'drawn'; url: string; blob: Blob; contentType: string; fileName: string | null }
  /** The read was refused, held or unreachable, which is one outcome for a reader. */
  | { kind: 'failed' };

/** The file name the answer names itself by, or null where nothing said. */
function namedBy(response: Response): string | null {
  const disposition = response.headers?.get('content-disposition') ?? '';
  return /filename="([^"]+)"/.exec(disposition)?.[1] ?? null;
}

/**
 * The bytes under one address, fetched once. A null address is nothing to ask
 * for, which reads as fetching and issues no request.
 *
 * 🔨 The answer is keyed by the address it came from and the state is derived
 * from that key, never written back by an effect: bytes under a different
 * address are not this address's bytes, so a changed address is fetching again
 * on the render that changed it.
 */
export function useFetchedBlob(url: string | null): FetchedBlob {
  const [answered, setAnswered] = useState<{ key: string; blob: FetchedBlob } | null>(null);
  const blob: FetchedBlob = url !== null && answered?.key === url ? answered.blob : { kind: 'fetching' };

  useEffect(() => {
    if (url === null) return undefined;
    let made: string | null = null;
    let live = true;
    const controller = new AbortController();
    apiFetch(url, { signal: controller.signal })
      .then(async (response) => {
        // A refusal, a held version or a type nothing serves: the reader is told, not left waiting.
        if (!response.ok) return live ? setAnswered({ key: url, blob: { kind: 'failed' } }) : undefined;
        const bytes = await response.blob();
        if (!live) return undefined;
        made = URL.createObjectURL(bytes);
        return setAnswered({ key: url, blob: { kind: 'drawn', url: made, blob: bytes, contentType: bytes.type, fileName: namedBy(response) } });
      })
      .catch((error: unknown) => {
        reportCaughtInBrowser('files.fetchedBlob', error);
        if (live && !controller.signal.aborted) setAnswered({ key: url, blob: { kind: 'failed' } });
      });
    return () => {
      live = false;
      controller.abort();
      if (made !== null) URL.revokeObjectURL(made);
    };
  }, [url]);

  return blob;
}
